import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Form,
  Actions,
  Fieldset,
  Loading,
  Pagetitle,
  FriendlyCaptcha,
} from '@ktag/styleguide/dist/react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { EditProfileRequest } from '@ktag/newsletters-sdk/dist';
import { ProfileData, Salutation } from 'types/api.types';
import AppContext from 'AppContext';
import DynamicFormField from 'components/dynamicFormField/DynamicFormField';
import mergeDynamicFormFields from 'utils/mergeDynamicFormFields';
import EditProgress from './EditProgress';
import EditInfoBox from './EditInfoBox';
import DataPolicy from '../dataPolicy/DataPolicy';
import BackToOverview from '../backToOverview/BackToOverview';

type FormValues = Omit<EditProfileRequest, 'bksakTheaterfunken'> & {
  bksakTheaterfunken?: 'on' | null;
  friendlyCaptcha?: string;
};

const Edit = () => {
  const { t } = useTranslation('newsletter');
  const [editStep, setEditStep] = useState(2);
  const { profileApi, config, siteKey } = useContext(AppContext);
  const [searchParams] = useSearchParams();

  const { data, isLoading } = useQuery<ProfileData>(
    ['profile', searchParams.get('hash')],
    () => profileApi.getProfile(searchParams.get('hash')),
  );

  const initialValues = {
    ...data,
    bksakTheaterfunken: data?.bksakTheaterfunken ? 'on' : 'off',
  };

  delete initialValues.newsletters;

  const getDynamicFormFields = () => {
    const dynamicFormFields = config.lists
      .filter(
        (item) => data?.newsletters.map((newsletter) => newsletter.id).includes(item.id),
      )
      .map(({ id, fields }) =>
        fields.map((field) => {
          const newsletterItem = data?.newsletters.find(
            (newsletter) => newsletter.id === id,
          );
          return {
            ...field,
            newsletterLabels: newsletterItem ? [newsletterItem.name] : [],
          };
        }),
      )
      .flat();

    return mergeDynamicFormFields(dynamicFormFields);
  };

  const editProfile = useMutation<void, void, EditProfileRequest>({
    mutationFn: (profileDataSubmitValues) =>
      profileApi.editProfile(searchParams.get('hash'), profileDataSubmitValues),
  });

  const onSubmitProfileData = async ({
    bksakTheaterfunken,
    friendlyCaptcha,
    ...rest
  }: FormValues) => {
    const editProfileRequest: EditProfileRequest = {
      ...rest,
      captcha: friendlyCaptcha,
    };

    if (!editProfileRequest.salutation) {
      editProfileRequest.salutation = Salutation.NoSalutation;
    }

    if (bksakTheaterfunken !== undefined) {
      editProfileRequest.bksakTheaterfunken = bksakTheaterfunken === 'on';
    }

    editProfile.mutate(editProfileRequest);
    setEditStep(3);
  };

  return (
    <div className="edit richtext">
      <Pagetitle additionalClassNames="edit__header">{t('edit_header')}</Pagetitle>
      {isLoading || editProfile.isLoading ? (
        <Loading size="medium" />
      ) : (
        <>
          <EditProgress editStep={editStep} isSuccess={editProfile.isSuccess} />
          <EditInfoBox
            editStep={editStep}
            isSuccess={editProfile.isSuccess}
            data={data}
          />
        </>
      )}
      {editStep === 2 && !isLoading && (
        <Form initialValues={initialValues} onSubmit={onSubmitProfileData}>
          <Fieldset>
            {config.defaultFields.concat(getDynamicFormFields()).map((item) => (
              <DynamicFormField fieldItem={item} key={item.name} />
            ))}
          </Fieldset>

          <p className="edit__form-required-text">
            {t('newsletter_required_fields_text')}
          </p>

          <div className="edit__form-captcha-container">
            <FriendlyCaptcha siteKey={siteKey} />
          </div>

          <div className="edit__form-footer-container">
            <DataPolicy />
            <Actions label={t('edit_edit')} />
          </div>
        </Form>
      )}
      {editStep === 3 && <BackToOverview />}
    </div>
  );
};

export default Edit;
